import { Card } from '@/components/Elements'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { getStage } from '@/features/offers/helpers'
import usePrintCaptureMode from '@/features/offers/hooks/usePrintCaptureMode'
import OfferGCMaterialTable from './components/OfferGCMaterialTable'
import { authSelector } from '@/stores/auth/authSlice'
import { isNumber } from '@/utils/helper'
import { roundNumber } from '@/utils/stringHelper'
import { Button, ButtonGroup, Flex } from '@chakra-ui/react'
import { OfferStage, OfferStatus, PRStatus } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

const GCMaterialTab = () => {
  const { user } = useSelector(authSelector)

  const { getValues, setValue } = useFormContext()
  const columnHelper = createColumnHelper()

  const watchedGCMaterialSubmissions = useWatch({ name: `sourcing.GCMaterialSubmissions` })
  const GCMaterialSubmissions = useMemo(() => watchedGCMaterialSubmissions ?? [], [watchedGCMaterialSubmissions])
  const projectManager = useWatch({ name: 'manager' })

  const subSystems = useWatch({ name: 'subSystems' })

  const status = useWatch({ name: 'status' })

  const offerStage = getStage(status)

  const [selectedSubSystemIndex, setSelectedSubSystemIndex] = useState(null)
  const isPrintCaptureMode = usePrintCaptureMode()
  // Print capture should always go through every subsystem, regardless of whatever filter the
  // user had active when they opened the print modal.
  const effectiveSelectedSubSystemIndex = isPrintCaptureMode ? null : selectedSubSystemIndex

  const { append: appendPRSubmission } = useFieldArray({
    name: `sourcing.GCMaterialSubmissions`,
  })

  const { useFetchAllContributorGroups } = useContributorGroup()

  const { data: contributorGroupsResponse } = useFetchAllContributorGroups({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  //Translated
  const hasDraftPR = GCMaterialSubmissions.some((submission) => submission.status === PRStatus.DRAFT)

  const ongoingItems = useMemo(() => {
    if (GCMaterialSubmissions.length === 0) return []

    const nonClosedPR = GCMaterialSubmissions.find((submission) => submission.status !== PRStatus.APPROVED)
    if (!nonClosedPR) return []

    return nonClosedPR.requestedDetails
  }, [GCMaterialSubmissions])

  const PM040GCMaterialData = useMemo(() => {
    if (!Array.isArray(subSystems)) return []

    const data = []

    subSystems.forEach((subSystem, subSystemIndex) => {
      if (!Array.isArray(subSystem.PM040GCMaterials)) return

      subSystem.PM040GCMaterials.forEach((material) => {
        const masterDetail = material.freezedDetail ? material.freezedDetail : material.detail

        const hasMultipleMappedDetail = Array.isArray(material.allDetails) && material.allDetails.length > 1
        const masterUnitCost = isNumber(masterDetail?.unitCost) ? masterDetail?.unitCost : undefined

        data.push({
          ...material,
          totalCost: material.unitCost * material.quantity,
          subSystemIndex: `1.${subSystemIndex + 1}`,
          masterDescription: masterDetail?.description || ``,
          masterUnitCost: masterUnitCost,
          masterCurrency: masterDetail?.currency,
          masterUnit: masterDetail?.unit,
          masterSAPMaterialNumber: masterDetail?.SAPMaterialNumber,
          masterVendor: masterDetail?.vendor,
          masterSEN: masterDetail?.SEN,
          masterArticleType: masterDetail?.articleType,
          mappedDetails: material?.allDetails || [],
          masterIsActive: masterDetail?.status === 'active',
          masterCtyOfOrigin: masterDetail?.ctyOfOrigin,
          masterDiscontinued: masterDetail?.discontinued ?? false,
        })
      })
    })
    return data
  }, [subSystems])

  const PM100GCMaterialData = useMemo(() => {
    if (!Array.isArray(subSystems)) return []

    const data = []

    subSystems.forEach((subSystem, subSystemIndex) => {
      if (!Array.isArray(subSystem.PM100GCMaterials)) return

      subSystem.PM100GCMaterials.forEach((material) => {
        const masterDetail = material.freezedDetail ? material.freezedDetail : material.detail

        const hasMultipleMappedDetail = Array.isArray(material.allDetails) && material.allDetails.length > 1
        const masterUnitCost = isNumber(masterDetail?.unitCost) ? masterDetail?.unitCost : undefined

        data.push({
          ...material,
          totalCost: material.unitCost * material.quantity,
          subSystemIndex: `1.${subSystemIndex + 1}`,
          masterDescription: masterDetail?.description || ``,
          masterUnitCost: masterUnitCost,
          masterCurrency: masterDetail?.currency,
          masterUnit: masterDetail?.unit,
          masterSAPMaterialNumber: masterDetail?.SAPMaterialNumber,
          masterVendor: masterDetail?.vendor,
          masterSEN: masterDetail?.SEN,
          masterArticleType: masterDetail?.articleType,
          masterMOQ: masterDetail?.MOQ,
          mappedDetails: material?.allDetails || [],
          masterIsActive: masterDetail?.status === 'active',
          masterCtyOfOrigin: masterDetail?.ctyOfOrigin,
          masterDiscontinued: masterDetail?.discontinued ?? false,
        })
      })
    })

    return data
  }, [subSystems])

  const filteredPM040Data = useMemo(() => {
    if (effectiveSelectedSubSystemIndex === null) return PM040GCMaterialData
    const label = `1.${effectiveSelectedSubSystemIndex + 1}`
    return PM040GCMaterialData.filter((item) => item.subSystemIndex === label)
  }, [PM040GCMaterialData, effectiveSelectedSubSystemIndex])

  const filteredPM100Data = useMemo(() => {
    if (effectiveSelectedSubSystemIndex === null) return PM100GCMaterialData
    const label = `1.${effectiveSelectedSubSystemIndex + 1}`
    return PM100GCMaterialData.filter((item) => item.subSystemIndex === label)
  }, [PM100GCMaterialData, effectiveSelectedSubSystemIndex])

  return (
    <Flex gap="5" direction={'column'}>
      {!isPrintCaptureMode && Array.isArray(subSystems) && subSystems.length > 0 && (
        <ButtonGroup size="sm" flexWrap="wrap" gap="2">
          <Button
            colorScheme={selectedSubSystemIndex === null ? 'blue' : 'gray'}
            variant={selectedSubSystemIndex === null ? 'solid' : 'outline'}
            onClick={() => setSelectedSubSystemIndex(null)}
          >
            All
          </Button>
          {subSystems.map((subSystem, index) => (
            <Button
              key={index}
              colorScheme={selectedSubSystemIndex === index ? 'blue' : 'gray'}
              variant={selectedSubSystemIndex === index ? 'solid' : 'outline'}
              onClick={() => setSelectedSubSystemIndex(selectedSubSystemIndex === index ? null : index)}
            >
              {`1.${index + 1}`} {subSystem.projectName}
            </Button>
          ))}
        </ButtonGroup>
      )}
      <Card title={'PM040'} defaultIsOpen={offerStage === OfferStage.QUOTATION} canCollapse>
        <OfferGCMaterialTable
          data={filteredPM040Data}
          isFreezed={
            ![OfferStatus.QUOTATION_DRAFT, OfferStatus.QUOTATION_SUBMITTED, OfferStatus.QUOTATION_REJECTED].includes(
              status
            )
          }
        />
      </Card>
      <Card title={'PM100'} defaultIsOpen={offerStage === OfferStage.BOOKING} canCollapse>
        <OfferGCMaterialTable
          data={filteredPM100Data}
          isFreezed={![OfferStatus.BOOKING_DRAFT, OfferStatus.BOOKING_REJECTED].includes(status)}
        />
      </Card>
    </Flex>
  )
}

export default GCMaterialTab
